import { useMemo, ReactNode } from 'react';
import { useHomeConfig } from './useHomeConfig';
import { WelcomeBanner } from './components/WelcomeBanner';
import { DynamicSection } from './components/DynamicSection';
import { PluginWidget } from './components/PluginWidget';

export interface HomePageProps {
  leftWidgets?: Array<{
    element: ReactNode;
    title?: string;
    priority: number;
  }>;
  rightWidgets?: Array<{
    element: ReactNode;
    title?: string;
    priority: number;
  }>;
}

/**
 * Home page component with dynamic configuration
 *
 * Reads configuration from app-config.yaml via useHomeConfig hook
 * and renders sections, plugins, and extension widgets based on configuration.
 */
export const HomePage = ({
  leftWidgets = [],
  rightWidgets = [],
}: HomePageProps) => {
  const config = useHomeConfig();

  // Sort sections and plugins by priority and column
  const { leftSections, rightSections, leftPlugins, rightPlugins } =
    useMemo(() => {
      const sections = config.sections || [];
      const plugins = config.plugins || [];

      return {
        leftSections: sections
          .filter(s => s.column === 'left' && s.enabled)
          .sort((a, b) => a.priority - b.priority),
        rightSections: sections
          .filter(s => s.column === 'right' && s.enabled)
          .sort((a, b) => a.priority - b.priority),
        leftPlugins: plugins
          .filter(p => p.column === 'left' && p.enabled)
          .sort((a, b) => a.priority - b.priority),
        rightPlugins: plugins
          .filter(p => p.column === 'right' && p.enabled)
          .sort((a, b) => a.priority - b.priority),
      };
    }, [config.sections, config.plugins]);

  const spacing = config.settings?.spacing || 6;

  // Convert spacing value to rem for inline styles
  const spacingRem = `${spacing * 0.25}rem`;

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--appvia-background-default)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {config.banner?.enabled && <WelcomeBanner config={config.banner} />}

        {config.layout?.type === 'two-column' && (
          <div
            className="grid grid-cols-1 lg:grid-cols-3"
            style={{ gap: spacingRem }}
          >
            <div
              className="lg:col-span-2"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacingRem,
              }}
            >
              {leftSections.map(section => (
                <DynamicSection key={section.id} section={section} />
              ))}
              {leftPlugins.map(plugin => (
                <PluginWidget key={plugin.id} plugin={plugin} />
              ))}
              {leftWidgets.map((widget, i) => (
                <div key={`widget-left-${i}`}>{widget.element}</div>
              ))}
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacingRem,
              }}
            >
              {rightSections.map(section => (
                <DynamicSection key={section.id} section={section} />
              ))}
              {rightPlugins.map(plugin => (
                <PluginWidget key={plugin.id} plugin={plugin} />
              ))}
              {rightWidgets.map((widget, i) => (
                <div key={`widget-right-${i}`}>{widget.element}</div>
              ))}
            </div>
          </div>
        )}

        {config.layout?.type === 'single-column' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacingRem,
            }}
          >
            {[...leftSections, ...rightSections]
              .sort((a, b) => a.priority - b.priority)
              .map(section => (
                <DynamicSection key={section.id} section={section} />
              ))}
            {[...leftPlugins, ...rightPlugins]
              .sort((a, b) => a.priority - b.priority)
              .map(plugin => (
                <PluginWidget key={plugin.id} plugin={plugin} />
              ))}
            {[...leftWidgets, ...rightWidgets]
              .sort((a, b) => a.priority - b.priority)
              .map((widget, i) => (
                <div key={`widget-${i}`}>{widget.element}</div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
